/**
 * Guardian Portfolio Pricing Calculator (v2)
 *
 * Flow: pick modules → build a shared fractional team → set number of
 * companies → volume discount applies. Training and legal route separately.
 * Config constants + subcomponents live in ./pricing/.
 */
import { useMemo, useRef, useState } from "react";
import {
  Check, Plus, Sparkles, Info, Users, Building2, Scale,
  Download, Minus, Save, Edit3,
} from "lucide-react";
import { toast } from "sonner";
import { E, useEditableContent } from "@/components/EditableText";
import { savePortfolioQuote } from "@/lib/api";
import {
  SUN_DEEP, SUN, INK, EMBER, CREAM,
  CORE_MONTHLY, CORE_INCLUSIONS, MODULES, HV,
  COMPANY_TIERS, findCompanyTier, ROLES, TRAINING_TIERS, trainingRate,
  SCREENING_LIMITS, CC_COSTS, fmt, fmt0,
} from "./pricing/config";
import { ModulePriceEditor } from "./pricing/ModulePriceEditor";
import { MarginPanel } from "./pricing/MarginPanel";
import { SaveQuoteForm } from "./pricing/SaveQuoteForm";
import { PrintQuote } from "./pricing/PrintQuote";

/* =========================================================================
 * COMPONENT
 * ========================================================================= */

export default function AlaCartePricing({
  modules: modulesProp = MODULES,
  defaultCompanies = 5,
  copy = {},
  quoteExtras = null,
}) {
  const C = {
    headline: "Build the program you need. Nothing you don't.",
    subhead: "Pick the modules that fit each portfolio company's risk. Build one shared fractional team over the whole book. Set how many companies you're running — the more you cover, the lower the effective per-company rate.",
    coreTitle: "Guardian Portfolio",
    companiesStep: "Step 3 · Portfolio size · Volume discount",
    companiesTitle: "How many companies?",
    companiesNote: "The volume discount is flat by tier and applies to the per-company stack. The team you build below is shared across all of them.",
    unitLabel: "companies",
    teamStep: "Step 4 · Build your team · Shared across portfolio",
    teamIntro: "One shared team across your whole portfolio — not per company.",
    teamSubtotalLabel: "Team subtotal (portfolio-wide, not multiplied by companies)",
    individualLabel: "Individual company",
    portfolioLabel: "Portfolio",
    individualNote: "Pricing a single company — the full per-company stack applies with no volume discount. Volume tiers kick in from 2+ companies; switch to Portfolio to see them.",
    ...copy,
  };
  const { editMode, overrides, save: saveOverride, reset: resetOverride } = useEditableContent();

  const [selected, setSelected] = useState({});          // {moduleKey: true}
  const [highVolume, setHighVolume] = useState(false);
  const [viewMode, setViewMode] = useState(defaultCompanies > 1 ? "portfolio" : "individual");
  const [companies, setCompanies] = useState(defaultCompanies);
  const [roles, setRoles] = useState(() =>
    Object.fromEntries(ROLES.map((r) => [r.key, { on: false, mode: "committed", hours: r.default }])),
  );
  const [seats, setSeats] = useState(15);
  const [editingPrice, setEditingPrice] = useState(null); // moduleKey being edited
  const [savingQuote, setSavingQuote] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ open: false, email: "", name: "", org: "", note: "", saved: null });
  const printRef = useRef(null);

  /* ---- Resolve module prices with admin overrides layered on top ---- */
  const priceKey = (k) => `pricing.module.${k}.price`;
  const modulePrice = (m) => {
    const raw = overrides?.[priceKey(m.key)];
    const parsed = raw != null ? parseInt(String(raw).replace(/[^\d]/g, ""), 10) : NaN;
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : m.price;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeModules = useMemo(() => modulesProp.map((m) => ({ ...m, price: modulePrice(m) })), [modulesProp, overrides]);

  /* ---- Derived math (uses activeModules with override-aware prices) ---- */
  const isIndividual = viewMode === "individual";
  const effectiveCompanies = isIndividual ? 1 : companies;
  const modulesSubtotal = activeModules.reduce((a, m) => a + (selected[m.key] ? m.price : 0), 0);
  const perCompanyStack = CORE_MONTHLY + modulesSubtotal;

  const tier = findCompanyTier(effectiveCompanies);
  const isCustom = !!tier.custom;
  const discount = tier.discount || 0;

  const effectivePerCo = perCompanyStack * (1 - discount);
  const perCoSubtotal = effectivePerCo * effectiveCompanies;

  // HV surcharge (only when AML selected)
  const hvSurcharge = highVolume && selected.aml
    ? HV.base + Math.max(0, effectiveCompanies - HV.baseCompanies) * HV.perCompanyOver
    : 0;

  // Team subtotal (portfolio-level, committed only)
  const teamLines = useMemo(() => ROLES.map((r) => {
    const s = roles[r.key];
    if (!s?.on || s.mode !== "committed") return { ...r, mode: s?.mode || "committed", on: !!s?.on, hours: s?.hours || 0, monthly: 0 };
    const monthly = s.hours * 4 * r.committed;
    return { ...r, mode: "committed", on: true, hours: s.hours, monthly };
  }), [roles]);
  const activeTeamLines = useMemo(() => teamLines.filter((l) => l.on), [teamLines]);
  const teamSubtotal = teamLines.reduce((a, l) => a + l.monthly, 0);

  const monthlyPlatform = isCustom ? null : perCoSubtotal + hvSurcharge + teamSubtotal;
  const annualPlatform = monthlyPlatform === null ? null : monthlyPlatform * 12;

  // Training (annual, separate)
  const trPerSeat = trainingRate(seats);
  const trainingAnnual = seats * trPerSeat;

  /* ---- Handlers ---- */
  const toggleModule = (k) => setSelected((s) => ({ ...s, [k]: !s[k] }));
  const bumpCompanies = (d) => setCompanies((c) => Math.max(1, Math.min(200, c + d)));
  const switchView = (mode) => {
    setViewMode(mode);
    if (mode === "portfolio" && companies < 2) setCompanies(Math.max(defaultCompanies, 5));
  };
  const updateRole = (k, patch) => setRoles((r) => ({ ...r, [k]: { ...r[k], ...patch } }));
  const buildWholeTeam = () => {
    setRoles(Object.fromEntries(
      ROLES.map((r) => [r.key, { on: true, mode: "committed", hours: r.default }]),
    ));
  };

  const commitPriceEdit = async (key, raw) => {
    const parsed = parseInt(String(raw).replace(/[^\d]/g, ""), 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error("Enter a whole dollar amount");
      return;
    }
    const ok = await saveOverride(priceKey(key), String(parsed));
    if (ok) {
      toast.success(`${modulesProp.find((x) => x.key === key)?.label || key} → $${parsed}`);
    } else {
      toast.error("Could not save price override");
    }
    setEditingPrice(null);
  };
  const revertPrice = async (key) => {
    await resetOverride(priceKey(key));
    toast.success("Reverted to default");
    setEditingPrice(null);
  };

  const handleDownloadQuote = () => {
    document.body.setAttribute("data-print-quote", "true");
    setTimeout(() => {
      window.print();
      // reset shortly after print dialog closes
      setTimeout(() => document.body.removeAttribute("data-print-quote"), 500);
    }, 60);
  };

  const handleSaveQuote = async () => {
    setSavingQuote(true);
    try {
      const selectedModuleObjs = activeModules
        .filter((m) => selected[m.key])
        .map((m) => ({ key: m.key, label: m.label, price: m.price }));
      const teamPayload = teamLines
        .filter((l) => l.on)
        .map((l) => ({
          key: l.key,
          label: l.label,
          mode: l.mode,
          hours: l.mode === "committed" ? l.hours : 0,
          monthly: l.mode === "committed" ? l.monthly : 0,
        }));
      const payload = {
        recipient_email: quoteForm.email.trim() || null,
        recipient_name: quoteForm.name.trim() || null,
        recipient_org: quoteForm.org.trim() || null,
        companies,
        per_company_stack: perCompanyStack,
        tier_label: tier.label,
        discount_pct: Math.round(discount * 100),
        is_custom: isCustom,
        monthly_platform: monthlyPlatform == null ? null : Math.round(monthlyPlatform),
        annual_platform: annualPlatform == null ? null : Math.round(annualPlatform),
        high_volume: highVolume && !!selected.aml,
        hv_surcharge: hvSurcharge,
        modules: selectedModuleObjs,
        team: teamPayload,
        team_subtotal: teamSubtotal,
        seats,
        training_annual: trainingAnnual,
        note: quoteForm.note.trim() || null,
        ...(quoteExtras || {}),
      };
      const r = await savePortfolioQuote(payload);
      toast.success("Quote saved — Colin will follow up.");
      setQuoteForm((f) => ({ ...f, saved: r?.id || true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save quote");
    } finally {
      setSavingQuote(false);
    }
  };

  /* ---- Margin math (admin only, never rendered on client view) ---- */
  const cc = useMemo(() => {
    // Rough per-company monthly ComplyCube cost estimate; used for internal margin math.
    const amlCost = selected.aml ? (CC_COSTS.onboarding_passive * SCREENING_LIMITS.aml.included) + (CC_COSTS.monitoring_profile * SCREENING_LIMITS.aml.included * 6) : 0;
    const vendorCost = selected.vendor ? (CC_COSTS.vendor_screen * SCREENING_LIMITS.vendor.included) : 0;
    const perCoScreeningCost = amlCost + vendorCost;
    const totalScreeningCost = perCoScreeningCost * effectiveCompanies;
    const revenue = (monthlyPlatform || 0);
    const laborBlended = teamSubtotal * 0.55; // rough burden
    const platformFixed = 400;
    const totalCost = totalScreeningCost + laborBlended + platformFixed;
    const gross = revenue - totalCost;
    const margin = revenue > 0 ? Math.round((gross / revenue) * 100) : 0;
    return { perCoScreeningCost, totalScreeningCost, laborBlended, platformFixed, totalCost, gross, margin };
  }, [selected, effectiveCompanies, monthlyPlatform, teamSubtotal]);

  /* =======================================================================
   *  RENDER
   * ======================================================================= */
  return (
    <section
      id="pricing"
      className="px-6 md:px-12 py-16 md:py-24"
      style={{ background: "rgba(255,255,255,0.55)", borderTop: "1px solid rgba(15,30,46,0.06)" }}
      data-testid="pricing-section"
    >
      {/* Print CSS — reveals ONLY the branded quote card when print is triggered from the Download button */}
      <style>{`
        @media print {
          body[data-print-quote="true"] { background: #FDFBF5 !important; }
          body[data-print-quote="true"] > *:not(script):not(style) { display: none !important; }
          body[data-print-quote="true"] [data-print-quote-card] {
            display: block !important;
            position: static !important;
            width: 100% !important;
            max-width: none !important;
            padding: 32px 40px !important;
            background: #FDFBF5 !important;
            color: #0F1E2E !important;
          }
          body[data-print-quote="true"] [data-print-quote-card] * { visibility: visible !important; }
        }
        [data-print-quote-card] { display: none; }
      `}</style>

      <div className="max-w-6xl mx-auto">
        <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN_DEEP }}>/ Pricing · à la carte</p>
        <h2 className="pcp-h text-4xl md:text-5xl mb-4 max-w-3xl">
          <E k="pricing.headline" defaultText={C.headline} as="span" multiline />
        </h2>
        <p className="text-base md:text-lg opacity-80 max-w-3xl mb-6 leading-relaxed">
          <E
            k="pricing.subhead"
            defaultText={C.subhead}
            as="span"
            multiline
          />
        </p>

        {/* ---- View toggle: Individual vs Portfolio ---- */}
        <div className="inline-flex rounded-lg p-1 mb-10" style={{ background: "rgba(15,30,46,0.07)", border: "1px solid rgba(15,30,46,0.12)" }} data-testid="pricing-view-toggle">
          {[["individual", C.individualLabel], ["portfolio", C.portfolioLabel]].map(([mode, label]) => {
            const active = viewMode === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => switchView(mode)}
                className="pcp-mono text-[11px] px-4 py-2 rounded-md transition-colors"
                style={{
                  background: active ? INK : "transparent",
                  color: active ? CREAM : INK,
                }}
                data-testid={`view-${mode}-btn`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* ---- Step 1: Core + Modules ---- */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Core (included, locked) */}
          <div className="pcp-card p-6" data-testid="pricing-included">
            <div className="flex items-center gap-2 mb-3">
              <Check className="h-4 w-4" style={{ color: SUN_DEEP }} strokeWidth={2} />
              <p className="pcp-mono text-[11px]" style={{ color: SUN_DEEP }}>Step 1 · Included · Core</p>
            </div>
            <h3 className="pcp-h text-2xl mb-2" style={{ color: INK }}>{C.coreTitle}</h3>
            <div className="text-3xl font-semibold mb-1" style={{ color: INK }}>
              {fmt(CORE_MONTHLY)}<span className="text-sm font-normal opacity-60"> / company / mo</span>
            </div>
            <p className="pcp-mono text-[10px] opacity-60 mb-4">Always included · never removed</p>
            <ul className="space-y-1.5 text-sm opacity-85">
              {CORE_INCLUSIONS.map((f, i) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: SUN_DEEP }} />
                  <span><E k={`pricing.core.f${i}`} defaultText={f} as="span" /></span>
                </li>
              ))}
            </ul>
          </div>

          {/* Modules */}
          <div className="pcp-card p-6" data-testid="pricing-managed">
            <div className="flex items-center gap-2 mb-3">
              <Plus className="h-4 w-4" style={{ color: SUN_DEEP }} strokeWidth={2} />
              <p className="pcp-mono text-[11px]" style={{ color: SUN_DEEP }}>Step 2 · Add modules · Per company</p>
            </div>
            <h3 className="pcp-h text-2xl mb-2" style={{ color: INK }}>Add modules</h3>
            <div className="text-3xl font-semibold mb-4" style={{ color: INK }}>
              +{fmt(modulesSubtotal)}<span className="text-sm font-normal opacity-60"> / company / mo</span>
            </div>
            <ul className="space-y-2">
              {activeModules.map((m) => {
                const on = !!selected[m.key];
                const isEditing = editingPrice === m.key;
                const defaultPrice = modulesProp.find((x) => x.key === m.key)?.price;
                const isOverridden = m.price !== defaultPrice;
                return (
                  <li key={m.key}>
                    <div
                      className="w-full text-left px-3 py-2 rounded-md transition"
                      style={{
                        background: on ? "rgba(245,180,0,0.15)" : "transparent",
                        border: `1px solid ${on ? SUN : "rgba(15,30,46,0.12)"}`,
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => toggleModule(m.key)}
                          className="flex items-center gap-2 text-sm flex-1 text-left"
                          data-testid={`module-toggle-${m.key}`}
                        >
                          <span
                            className="inline-flex items-center justify-center h-4 w-4 rounded-sm"
                            style={{
                              background: on ? SUN : "transparent",
                              border: `1px solid ${on ? SUN : "rgba(15,30,46,0.3)"}`,
                            }}
                          >
                            {on && <Check className="h-3 w-3" style={{ color: INK }} strokeWidth={3} />}
                          </span>
                          <span className={on ? "font-medium" : ""}>{m.label}</span>
                        </button>
                        {editMode && isEditing ? (
                          <ModulePriceEditor
                            initial={m.price}
                            onSave={(v) => commitPriceEdit(m.key, v)}
                            onCancel={() => setEditingPrice(null)}
                            onRevert={isOverridden ? () => revertPrice(m.key) : null}
                            testKey={m.key}
                          />
                        ) : (
                          <span className="flex items-center gap-1.5">
                            <span
                              className="pcp-mono text-[11px] opacity-70"
                              style={isOverridden ? { color: SUN_DEEP, fontWeight: 600 } : undefined}
                              data-testid={`module-price-${m.key}`}
                            >
                              {fmt(m.price)}
                            </span>
                            {editMode && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setEditingPrice(m.key); }}
                                className="p-1 rounded hover:bg-black/10 transition"
                                title="Edit price"
                                data-testid={`module-price-edit-${m.key}`}
                              >
                                <Edit3 className="h-3 w-3" style={{ color: SUN_DEEP }} />
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                      {m.footnote && (
                        <button
                          type="button"
                          onClick={() => toggleModule(m.key)}
                          className="block text-left w-full"
                        >
                          <p className="pcp-mono text-[10px] opacity-55 mt-1.5 ml-6 leading-snug">{m.footnote}</p>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* High-volume onboarding — attached to AML */}
            <div className="mt-4 pt-4 border-t border-black/10">
              <label className={`flex items-center gap-2 ${selected.aml ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                <input
                  type="checkbox"
                  checked={highVolume && !!selected.aml}
                  disabled={!selected.aml}
                  onChange={(e) => setHighVolume(e.target.checked)}
                  className="h-4 w-4"
                  style={{ accentColor: SUN_DEEP }}
                  data-testid="hv-onboarding-toggle"
                />
                <span className="text-xs font-medium">High-volume onboarding (KYC pipeline)</span>
              </label>
              <p className="text-[11px] opacity-60 mt-1 leading-snug">
                Attach to AML for end-user KYC pipelines. Adds {fmt(HV.base)} base + {fmt(HV.perCompanyOver)}/company over {HV.baseCompanies}.
              </p>
              {highVolume && selected.aml && (
                <p className="pcp-mono text-[10px] mt-2" style={{ color: EMBER }} data-testid="hv-surcharge">
                  + {fmt(hvSurcharge)}/mo surcharge (portfolio-wide)
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ---- Step 3: Companies + Volume Discount ---- */}
        <div className="pcp-card p-6 md:p-7 mt-6" data-testid="pricing-companies">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4" style={{ color: SUN_DEEP }} strokeWidth={2} />
            <p className="pcp-mono text-[11px]" style={{ color: SUN_DEEP }}>
              {isIndividual ? `Step 3 · ${C.individualLabel}` : C.companiesStep}
            </p>
          </div>
          {isIndividual ? (
            <div data-testid="pricing-individual-note">
              <h3 className="pcp-h text-2xl mb-2" style={{ color: INK }}>{C.individualLabel}</h3>
              <p className="text-sm opacity-70 leading-relaxed max-w-2xl mb-3">{C.individualNote}</p>
              <p className="pcp-mono text-[11px]" data-testid="effective-per-co">
                Stack: <span className="font-semibold">{fmt(perCompanyStack)}</span> / mo
              </p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="pcp-h text-2xl mb-2" style={{ color: INK }}>{C.companiesTitle}</h3>
              <p className="text-sm opacity-70 mb-4 leading-relaxed">
                {C.companiesNote}
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => bumpCompanies(-1)}
                  className="h-10 w-10 rounded-md flex items-center justify-center"
                  style={{ background: "rgba(15,30,46,0.06)" }}
                  aria-label="Decrease companies"
                  data-testid="companies-decrement"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={companies}
                  onChange={(e) => setCompanies(Math.max(1, Math.min(200, parseInt(e.target.value, 10) || 1)))}
                  className="h-10 w-24 text-center rounded-md font-semibold text-lg"
                  style={{ border: "1px solid rgba(15,30,46,0.2)", background: "#fff" }}
                  data-testid="companies-input"
                />
                <button
                  type="button"
                  onClick={() => bumpCompanies(1)}
                  className="h-10 w-10 rounded-md flex items-center justify-center"
                  style={{ background: "rgba(15,30,46,0.06)" }}
                  aria-label="Increase companies"
                  data-testid="companies-increment"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <span className="text-sm opacity-60 ml-2">{C.unitLabel}</span>
              </div>
            </div>
            <div>
              <p className="pcp-mono text-[10px] opacity-60 mb-2">Tier</p>
              <ul className="space-y-1 text-xs">
                {COMPANY_TIERS.map((t) => {
                  const active = companies >= t.min && companies <= t.max;
                  return (
                    <li
                      key={t.label}
                      className="flex justify-between items-center px-2 py-1 rounded"
                      style={{
                        background: active ? "rgba(245,180,0,0.15)" : "transparent",
                        border: `1px solid ${active ? SUN : "transparent"}`,
                      }}
                      data-testid={`company-tier-${t.label.replace(/[^\d+]/g, "").replace("+", "plus") || "range"}`}
                    >
                      <span className={active ? "font-semibold" : "opacity-70"}>{t.label} companies</span>
                      <span className={active ? "font-semibold" : "opacity-70"}>
                        {t.custom ? "Contact us" : `${Math.round(t.discount * 100)}% off`}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {!isCustom && (
                <p className="pcp-mono text-[11px] mt-3" data-testid="effective-per-co">
                  Effective: <span className="font-semibold">{fmt(effectivePerCo)}</span> / company / mo
                  {discount > 0 && <span className="opacity-60"> ({Math.round(discount * 100)}% off {fmt(perCompanyStack)})</span>}
                </p>
              )}
              {isCustom && (
                <p className="pcp-mono text-[11px] mt-3" style={{ color: EMBER }} data-testid="contact-us-note">
                  51+ companies — contact us for portfolio pricing.
                </p>
              )}
            </div>
          </div>
          )}
        </div>

        {/* ---- Step 4: Team Builder ---- */}
        <div className="pcp-card p-6 md:p-7 mt-6" data-testid="pricing-team">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" style={{ color: SUN_DEEP }} strokeWidth={2} />
              <p className="pcp-mono text-[11px]" style={{ color: SUN_DEEP }}>{C.teamStep}</p>
            </div>
            <button
              type="button"
              onClick={buildWholeTeam}
              className="pcp-mono text-[11px] px-3 py-1.5 rounded-md"
              style={{ background: INK, color: CREAM }}
              data-testid="build-whole-team"
            >
              Build the whole team →
            </button>
          </div>
          <h3 className="pcp-h text-2xl mb-2" style={{ color: INK }}>Assemble a shared fractional team</h3>
          <p className="text-sm opacity-70 leading-relaxed mb-5 max-w-3xl">
            {C.teamIntro} Committed hours lock our program rate
            (<span className="font-semibold">$150/hr senior · $75/hr ops</span>). Ad-hoc is
            <span className="font-semibold"> $250 · $125/hr</span> with no monthly commitment.
          </p>

          <div className="space-y-3">
            {ROLES.map((r) => {
              const s = roles[r.key];
              const on = s.on;
              const committed = s.mode === "committed";
              const monthly = on && committed ? s.hours * 4 * r.committed : 0;
              return (
                <div
                  key={r.key}
                  className="p-4 rounded-lg"
                  style={{
                    background: on ? "rgba(245,180,0,0.08)" : "rgba(15,30,46,0.03)",
                    border: `1px solid ${on ? SUN : "rgba(15,30,46,0.1)"}`,
                  }}
                  data-testid={`role-${r.key}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-[200px]">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => updateRole(r.key, { on: e.target.checked })}
                        className="h-4 w-4"
                        style={{ accentColor: SUN_DEEP }}
                        data-testid={`role-toggle-${r.key}`}
                      />
                      <span className={on ? "font-semibold" : ""}>{r.label}</span>
                    </label>

                    {on && (
                      <div className="flex items-center gap-2">
                        <div className="inline-flex rounded-md" style={{ border: `1px solid ${INK}22` }}>
                          <button
                            type="button"
                            onClick={() => updateRole(r.key, { mode: "committed" })}
                            className="pcp-mono text-[10px] px-2.5 py-1"
                            style={{
                              background: committed ? INK : "transparent",
                              color: committed ? CREAM : INK,
                            }}
                            data-testid={`role-mode-committed-${r.key}`}
                          >
                            Committed
                          </button>
                          <button
                            type="button"
                            onClick={() => updateRole(r.key, { mode: "adhoc" })}
                            className="pcp-mono text-[10px] px-2.5 py-1"
                            style={{
                              background: !committed ? INK : "transparent",
                              color: !committed ? CREAM : INK,
                            }}
                            data-testid={`role-mode-adhoc-${r.key}`}
                          >
                            Ad-hoc
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {on && (
                    <div className="mt-3 grid md:grid-cols-2 gap-4">
                      {committed ? (
                        <>
                          <div>
                            <label className="text-[11px] opacity-70">Hours / week: <span className="font-semibold">{s.hours}</span></label>
                            <input
                              type="range"
                              min={r.min}
                              max={r.max}
                              value={s.hours}
                              onChange={(e) => updateRole(r.key, { hours: parseInt(e.target.value, 10) })}
                              className="w-full mt-1"
                              style={{ accentColor: SUN_DEEP }}
                              data-testid={`role-hours-${r.key}`}
                            />
                            <div className="flex justify-between pcp-mono text-[10px] opacity-50">
                              <span>{r.min}</span><span>{r.max}</span>
                            </div>
                          </div>
                          <div className="text-right md:text-right">
                            <p className="pcp-mono text-[10px] opacity-60">Committed monthly</p>
                            <p className="pcp-h text-2xl font-semibold" style={{ color: INK }} data-testid={`role-monthly-${r.key}`}>
                              {fmt(monthly)}
                            </p>
                            <p className="pcp-mono text-[10px] opacity-50">
                              {s.hours} hrs/wk × 4 × {fmt0(r.committed)}/hr
                            </p>
                          </div>
                        </>
                      ) : (
                        <div className="md:col-span-2">
                          <p className="pcp-mono text-[11px] opacity-70">
                            Ad-hoc: <span className="font-semibold">{fmt0(r.adhoc)}/hr</span> as used. No monthly commitment; billed after the work.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex justify-between items-baseline">
            <p className="pcp-mono text-[11px] opacity-60">{C.teamSubtotalLabel}</p>
            <p className="text-2xl font-semibold" style={{ color: INK }} data-testid="team-subtotal">{fmt(teamSubtotal)}<span className="text-sm opacity-60"> / mo</span></p>
          </div>
        </div>

        {/* ---- Step 5: Legal (separate) ---- */}
        <div
          className="pcp-card p-6 mt-6"
          style={{ borderColor: EMBER, background: "rgba(255,123,31,0.05)" }}
          data-testid="pricing-legal"
        >
          <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: EMBER, color: "#fff" }}>
              <Scale className="h-5 w-5" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <p className="pcp-mono text-[11px] mb-1" style={{ color: EMBER }}>Add legal counsel</p>
              <h3 className="pcp-h text-xl mb-1" style={{ color: INK }}>Provided by CompliSun Legal, PLC</h3>
              <p className="text-sm opacity-80 leading-relaxed">
                A separate law firm engagement. The company is the client; not fee-shared with the platform.
                Core already carries an attorney-led escalation path — Legal, PLC is for full outside-counsel work.
              </p>
            </div>
            <a
              href="#contact"
              className="pcp-mono text-[11px] px-4 py-2 rounded-md whitespace-nowrap"
              style={{ background: EMBER, color: "#fff" }}
              data-testid="legal-request"
            >
              Request →
            </a>
          </div>
        </div>

        {/* ---- Step 6: Training (annual, separate) ---- */}
        <div className="pcp-card p-6 md:p-7 mt-6" data-testid="pricing-training">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4" style={{ color: SUN_DEEP }} strokeWidth={2} />
            <p className="pcp-mono text-[11px]" style={{ color: SUN_DEEP }}>Step 6 · Training · Per seat · Annual</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div>
              <h3 className="pcp-h text-2xl mb-2" style={{ color: INK }}>Team training</h3>
              <p className="text-sm opacity-70 mb-3 leading-relaxed">Billed annually. Volume-tiered per-seat rate.</p>
              <label className="block text-xs font-medium opacity-70 mb-1">Seats</label>
              <input
                type="range"
                min="1"
                max="200"
                value={seats}
                onChange={(e) => setSeats(parseInt(e.target.value, 10))}
                className="w-full"
                style={{ accentColor: SUN_DEEP }}
                data-testid="training-seats-slider"
              />
              <div className="flex justify-between pcp-mono text-[10px] opacity-60">
                <span>1</span><span>{seats}</span><span>200+</span>
              </div>
            </div>
            <div>
              <div className="text-3xl font-semibold mb-1" style={{ color: INK }} data-testid="training-annual">
                {fmt(trainingAnnual)}<span className="text-sm font-normal opacity-60"> / yr</span>
              </div>
              <p className="pcp-mono text-[10px] opacity-60 mb-3">{seats} seats × {fmt0(trPerSeat)}/seat/yr</p>
              <div className="space-y-1 text-xs opacity-80">
                {TRAINING_TIERS.map((t, i) => {
                  const prev = i === 0 ? 1 : TRAINING_TIERS[i - 1].upTo + 1;
                  const active = seats >= prev && seats <= t.upTo;
                  return (
                    <div key={t.upTo} className="flex justify-between" style={{ opacity: active ? 1 : 0.55 }}>
                      <span>{prev}–{t.upTo >= 9999 ? "∞" : t.upTo} seats</span>
                      <span className={active ? "font-semibold" : ""}>{fmt0(t.perSeat)}/seat/yr</span>
                    </div>
                  );
                })}
              </div>
              <p className="pcp-mono text-[10px] opacity-55 mt-3 leading-snug">
                Separate from monthly platform total. Not multiplied by companies.
              </p>
            </div>
          </div>
        </div>

        {/* ---- Summary block ---- */}
        <div className="mt-8 rounded-2xl p-6 md:p-8" style={{ background: INK, color: CREAM }} data-testid="pricing-summary">
          <div className="grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2">
              <p className="pcp-mono text-[11px] opacity-60 mb-3">Your program</p>
              <div className="space-y-1.5 text-sm">
                <SummaryLine label={`Per-company stack (Core + modules)`} value={`${fmt(perCompanyStack)} / co / mo`} />
                {isIndividual ? (
                  <SummaryLine label={C.individualLabel} value={`× 1 · no volume discount`} />
                ) : (
                  <>
                    <SummaryLine label={`Volume tier`} value={isCustom ? "51+ · custom" : `${tier.label} · ${Math.round(discount * 100)}% off`} />
                    <SummaryLine label={`Companies`} value={`× ${effectiveCompanies}`} />
                  </>
                )}
                {!isCustom && (
                  <SummaryLine
                    label={isIndividual ? `Company subtotal` : `Per-company subtotal (after discount)`}
                    value={`${fmt(perCoSubtotal)} / mo`}
                    strong
                  />
                )}
                {hvSurcharge > 0 && !isCustom && (
                  <SummaryLine label={`High-volume onboarding surcharge`} value={`+ ${fmt(hvSurcharge)} / mo`} />
                )}
                <div className="border-t pt-2 mt-2" style={{ borderColor: "rgba(255,255,255,0.15)" }}>
                  {activeTeamLines.map((l) => (
                    <SummaryLine
                      key={l.key}
                      label={`${l.label} · ${l.mode === "committed" ? `${l.hours} hrs/wk committed` : "ad-hoc"}`}
                      value={l.mode === "committed" ? `${fmt(l.monthly)} / mo` : "as used"}
                    />
                  ))}
                  <SummaryLine label={`Team subtotal (shared)`} value={`${fmt(teamSubtotal)} / mo`} strong />
                </div>
              </div>
            </div>
            <div className="text-left md:text-right">
              <p className="pcp-mono text-[11px] opacity-60 mb-2">Monthly platform total</p>
              {isCustom ? (
                <div className="pcp-h text-3xl md:text-4xl" style={{ color: SUN }} data-testid="pricing-total-monthly">Contact us</div>
              ) : (
                <>
                  <div className="pcp-h text-5xl md:text-6xl" data-testid="pricing-total-monthly">{fmt(monthlyPlatform)}</div>
                  <p className="pcp-mono text-[11px] opacity-60 mt-1" data-testid="pricing-total-annual">{fmt(annualPlatform)} / yr</p>
                </>
              )}
              <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}>
                <p className="pcp-mono text-[10px] opacity-60 mb-1">Training (separate · annual)</p>
                <p className="text-xl font-semibold" style={{ color: SUN }}>{fmt(trainingAnnual)}<span className="text-xs opacity-60"> / yr</span></p>
                <p className="pcp-mono text-[10px] opacity-50 mt-3">Legal via CompliSun Legal, PLC — separate engagement.</p>
              </div>
            </div>
          </div>

          {/* Download quote + Save quote CTAs */}
          <div className="mt-6 flex flex-wrap justify-between items-center gap-3 pt-6" style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}>
            <p className="pcp-mono text-[11px] opacity-60 flex items-center gap-1">
              <Info className="h-3 w-3" /> Estimates only. Final scope & pricing confirmed on kickoff call.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setQuoteForm((f) => ({ ...f, open: !f.open }))}
                className="pcp-mono text-[11px] px-4 py-2 rounded-md inline-flex items-center gap-2"
                style={{
                  background: quoteForm.open ? CREAM : "transparent",
                  color: quoteForm.open ? INK : CREAM,
                  border: `1px solid ${CREAM}`,
                }}
                data-testid="save-quote-toggle"
              >
                <Save className="h-3.5 w-3.5" /> {quoteForm.saved ? "Saved ✓" : "Save this quote"}
              </button>
              <button
                type="button"
                onClick={handleDownloadQuote}
                className="pcp-mono text-[11px] px-4 py-2 rounded-md inline-flex items-center gap-2"
                style={{ background: SUN, color: INK }}
                data-testid="download-quote-btn"
              >
                <Download className="h-3.5 w-3.5" /> Download PDF quote
              </button>
            </div>
          </div>

          {/* Save-quote inline form */}
          <SaveQuoteForm quoteForm={quoteForm} setQuoteForm={setQuoteForm} savingQuote={savingQuote} onSave={handleSaveQuote} />
        </div>

        {/* ---- Admin-only internal margin view ---- */}
        {editMode && <MarginPanel cc={cc} companies={effectiveCompanies} monthlyPlatform={monthlyPlatform} />}

        {/* ---- Print-only branded quote (revealed by handleDownloadQuote) ---- */}
        <PrintQuote
          ref={printRef}
          selectedModules={activeModules.filter((m) => selected[m.key])}
          perCompanyStack={perCompanyStack}
          companies={effectiveCompanies}
          tier={tier}
          isCustom={isCustom}
          discount={discount}
          effectivePerCo={effectivePerCo}
          perCoSubtotal={perCoSubtotal}
          hvSurcharge={hvSurcharge}
          highVolume={highVolume && !!selected.aml}
          teamLines={activeTeamLines}
          teamSubtotal={teamSubtotal}
          monthlyPlatform={monthlyPlatform}
          annualPlatform={annualPlatform}
          seats={seats}
          trainingAnnual={trainingAnnual}
          trPerSeat={trPerSeat}
        />
      </div>
    </section>
  );
}

/* ---------- small helpers ---------- */
function SummaryLine({ label, value, strong }) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <span className={strong ? "font-semibold" : "opacity-80"}>{label}</span>
      <span className={strong ? "font-semibold" : "opacity-90"}>{value}</span>
    </div>
  );
}
